<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {ChevronDown, Check} from "utruckin-icons";
import {IDropdown, IDropdownOptions} from "@/types/main";

const props = withDefaults(
    defineProps<IDropdownOptions>(),
    {
      position: 'top',
      label: "",
      items: () => [],
      modelValue: "",
      disabled: false,
    }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: string): void;
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const selectedLabel = computed(() => {
  const found = props.items.find((i) => i.value === props.modelValue);
  return found?.name || "";
});

const hasSelection = computed(() => selectedLabel.value.length > 0);

function toggle() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
}

function choose(item: IDropdown) {
  emit("update:modelValue", item.value);
  isOpen.value = false;
}

function onDocumentClick(e: MouseEvent) {
  const el = rootRef.value;
  if (!el) return;

  const target = e.target as Node | null;
  if (!target) return;

  if (!el.contains(target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});
</script>

<template>
  <div
      ref="rootRef"
      class="f-ui-dropdown"
      :class="{
      active: isOpen,
      disabled: disabled,
    }"
      @click="toggle"
  >
    <div class="value" :class="{ 'is-placeholder': !hasSelection }">
      {{ selectedLabel || label }}
    </div>

    <div class="icon" :class="{ rotate: isOpen }">
      <ChevronDown :size="20"/>
    </div>

    <div class="list" :class="position" v-if="isOpen">
      <div
          class="list__item"
          v-for="(item, index) in items"
          :key="item.value || index"
          @click.stop="choose(item)"
      >
        <span class="item-text">{{ item.name }}</span>
        <Check v-if="item.value === modelValue" class="check"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.f-ui-dropdown {
  width: 80px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 8px 12px;
  cursor: pointer;
  position: relative;
  background: var(--color-background);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--color-label);
  letter-spacing: 0;

  &:hover,
  &.active {
    border-color: var(--color-secondary-label);
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;

    .list {
      position: absolute;
      top: calc(100% + 4px);
      z-index: 3;
      left: 0;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      width: 100%;
      background: var(--color-white);

      &__item {
        padding: 8px 12px;
        color: var(--color-label);

        &:not(:last-child) {
          border-bottom: 1px solid var(--color-border);
        }
      }
    }
  }

  .value {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--color-label);

    &.is-placeholder {
      color: var(--color-secondary-label);
    }
  }

  .icon {
    display: flex;
    align-items: center;
    transition: 0.2s ease;

    &.rotate {
      transform: rotate(180deg);
    }
  }

  .list {
    position: absolute;
    left: 0;
    z-index: 90;
    width: 80px;
    max-height: 180px;
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: 12px;
    overflow: hidden;
    overflow-y: scroll;
    scrollbar-width: none;

    &.bottom {
      top: calc(100% + 6px);
    }

    &.top {
      bottom: calc(100% + 6px);
    }
  }

  .list__item {
    width: 80px;
    height: 40px;
    padding: 12px;
    color: var(--color-label);
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 1px solid var(--color-border);
    }

    &:hover {
      background: var(--color-hover);
    }
  }

  .check {
    width: 16px;
    color: var(--color-secondary-label);
    font-weight: 700;
  }
}
</style>
